import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import LocalView from './LocalView';
import { 
  getSystemProfile,
  getKeyUnmetNeeds,
  getSiteTreemap,
  getHospitalDistribution,
  getPhysicianDistribution
} from '../../../utils/api'; // Import API functions
import '../styles.css';

// Define unmet needs mapping: Frontend Display Name -> Backend Column Name
// This MUST match the keys expected by the backend API's UNMET_MAP
const UNMET_NEEDS_MAP = {
    "Excessive Steroid Use on Double Therapy": "Pct_ICS_LABA_High_Steroid_Usage",
    "Failure to Escalate after more than 3 ED/ER visits": "Pct_Failure_To_Escalate_With_3_Exacerbations",
    "Delay from Double to Advanced Therapy": "Pct_ICS_LABA_Escalation_Delay",
    "Biologic before triple": "Pct_Biologic_Before_Triple",
    "Open triple therapy": "Pct_ICS_LABA_With_LAMA"
};

// Frontend display names for unmet needs dropdown - Order derived from map keys
const unmetNeedsDisplayNames = Object.keys(UNMET_NEEDS_MAP);
const defaultPriorityUnmetNeed = unmetNeedsDisplayNames[0]; // Default to the first item in the new order


const SystemDetailPage = () => {
  const { systemName } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [selectedSystem, setSelectedSystem] = useState(location.state?.system || null);
  
  const [loading, setLoading] = useState(true); // Start loading immediately
  const [error, setError] = useState(null);
  const [priorityUnmetNeed, setPriorityUnmetNeed] = useState(defaultPriorityUnmetNeed);

  // State to hold data fetched from API endpoints
  const [systemProfileData, setSystemProfileData] = useState(null);
  const [keyUnmetNeedsData, setKeyUnmetNeedsData] = useState(null);
  const [siteTreemapData, setSiteTreemapData] = useState(null);
  const [hospitalDistributionData, setHospitalDistributionData] = useState(null);
  const [physicianDistributionData, setPhysicianDistributionData] = useState(null);

  // Effect 1: Fetch system-wide data (profile, key unmet needs summary, trends) when selectedSystem changes
  useEffect(() => {
    if (!selectedSystem) {
      setError('Health system data not available. Please select a system first.');
      setLoading(false);
      return;
    }

    const fetchSystemWideData = async () => {
      setLoading(true); // Main loading for system-wide data
      setError(null);
      const systemId = selectedSystem.id;

      try {
        const [profileRes, needsRes] = await Promise.all([
          getSystemProfile(systemId),
          getKeyUnmetNeeds(systemId),
        ]);

        if (!profileRes || !profileRes.system) throw new Error('Invalid profile data received');
        if (!needsRes?.key_unmet_needs || !needsRes?.trends) throw new Error('Invalid key unmet needs data received');

        setSystemProfileData(profileRes);
        setKeyUnmetNeedsData(needsRes);
      } catch (err) {
        console.error("Error fetching system-wide data:", err);
        setError(err.message || 'Failed to load system-wide details.');
        setSystemProfileData(null);
        setKeyUnmetNeedsData(null);
      } finally {
        // Set loading to false ONLY after this initial system-wide fetch completes.
        // Subsequent fetches for priority-need-specific data won't trigger this main loading overlay.
        setLoading(false); 
      }
    };

    fetchSystemWideData();
  }, [selectedSystem]);

  // Effect 2: Fetch priority-need-specific data (This must be retained)
  useEffect(() => {
    if (!selectedSystem || !priorityUnmetNeed) {
      setSiteTreemapData(null);
      setHospitalDistributionData(null);
      setPhysicianDistributionData(null);
      return; 
    }
    const fetchPriorityNeedData = async () => {
      const systemId = selectedSystem.id;
      // API calls use priorityUnmetNeed (the frontend display name)
      try {
        const [treemapRes, hospDistRes, physDistRes] = await Promise.all([
          getSiteTreemap(systemId, priorityUnmetNeed), 
          getHospitalDistribution(systemId, priorityUnmetNeed),
          getPhysicianDistribution(systemId, priorityUnmetNeed)
        ]);
        if (!treemapRes?.treemap) throw new Error('Invalid site treemap data received');
        if (!hospDistRes?.bins || !hospDistRes?.counts || !hospDistRes?.averages) throw new Error('Invalid hospital distribution data received');
        if (!physDistRes?.bins || !physDistRes?.counts || !physDistRes?.averages) throw new Error('Invalid physician distribution data received');
        setSiteTreemapData(treemapRes.treemap);
        setHospitalDistributionData(hospDistRes);
        setPhysicianDistributionData(physDistRes);
      } catch (err) {
        console.error("Error fetching priority-need specific data:", err);
        setSiteTreemapData(null);
        setHospitalDistributionData(null);
        setPhysicianDistributionData(null);
      }
    };
    fetchPriorityNeedData();
  }, [selectedSystem, priorityUnmetNeed]);

  // Handle priority unmet need change
  const handlePriorityNeedChange = (event) => {
    setPriorityUnmetNeed(event.target.value);
  };
  
  // Handle back button click
  const handleBackClick = () => {
    // Navigate back to the root of the asthma tracker
    navigate('/asthma_tracker'); 
  };

  // Initial loading state before selectedSystem is confirmed
  if (!selectedSystem && loading) {
      return <div className="loading-container">Loading health system identifier...</div>;
  }
  
  // If selectedSystem is null after initial check (e.g., direct access without state)
  if (!selectedSystem && !loading) {
       // If error is already set, show that, otherwise show a generic message
       return (
           <div className="error-container">
               <div>{error || 'No health system data found. Please return to selection.'}</div>
               <button className="back-button" onClick={handleBackClick}>
                   ← Back to Health Systems
               </button>
           </div>
       );
   }

  // Loading state while fetching data for the selected system
  if (loading) {
    return <div className="loading-container">Loading details for {selectedSystem.name}...</div>;
  }

  // Error state after attempting to fetch data
  if (error) {
    return (
      <div className="error-container">
        <div>Error loading data for {selectedSystem.name}: {error}</div>
        <button className="back-button" onClick={handleBackClick}>
          ← Back to Health Systems
        </button>
      </div>
    );
  }
  
  // Prepare props for the view components
  const viewProps = {
      selectedSystem, 
      priorityUnmetNeed,
      systemProfileData,
      keyUnmetNeedsData,
      siteTreemapData,
      hospitalDistributionData,
      physicianDistributionData,
      handlePriorityNeedChange,
      unmetNeedsDisplayNames
  };

  return (
    <div className="asthma-tracker-container">
      <div className="system-detail-header">
        <button className="back-button" onClick={handleBackClick}>
          ← Back to Health Systems
        </button>
        {/* Use name from selectedSystem object passed via state - REMOVING THIS H1 */}
        {/* <h1 className="page-title">{selectedSystem.name}</h1> */}
      </div>
      
      <div className="view-content">
        {/* Always render LocalView */}
        <LocalView {...viewProps} />
      </div>
    </div>
  );
};

export default SystemDetailPage; 